import { useState, useRef, useCallback } from 'react';
import type { LayoutBlock, EditorPage } from '@/mocks/editorLayout';

interface EditorCanvasProps {
  page: EditorPage;
  selectedBlockId: string | null;
  onSelectBlock: (id: string | null) => void;
  onUpdateBlock: (id: string, updates: Partial<LayoutBlock>) => void;
  onDropBlock: (type: string, x: number, y: number, extraData?: Record<string, string>) => void;
  zoom: number;
}

const COLS = 12;
const ROWS = 16;
const CELL_W = 72;
const CELL_H = 52;

const priorityBorder: Record<string, string> = {
  high: 'border-t-[#C41230]',
  medium: 'border-t-[#111111]',
  low: 'border-t-[#CCCCCC]',
};

const sectionColor: Record<string, string> = {
  Política: 'text-[#C41230]',
  Economia: 'text-[#1a5c1a]',
  Cultura: 'text-[#7a3a00]',
  Esportes: 'text-[#003a7a]',
  Ciência: 'text-[#4a007a]',
  Saúde: 'text-[#007a5a]',
  Internacional: 'text-[#5a5a00]',
};

function BlockRenderer({ block, isSelected, onSelect, onUpdate }: {
  block: LayoutBlock;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<LayoutBlock>) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ mouseX: number; mouseY: number; blockX: number; blockY: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (block.locked) return;
    e.stopPropagation();
    onSelect();
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      blockX: block.x,
      blockY: block.y,
    };
    setIsDragging(true);

    const handleMouseMove = (me: MouseEvent) => {
      if (!dragStart.current) return;
      const dx = Math.round((me.clientX - dragStart.current.mouseX) / CELL_W);
      const dy = Math.round((me.clientY - dragStart.current.mouseY) / CELL_H);
      const newX = Math.max(0, Math.min(COLS - block.w, dragStart.current.blockX + dx));
      const newY = Math.max(0, Math.min(ROWS - block.h, dragStart.current.blockY + dy));
      onUpdate({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStart.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const left = block.x * CELL_W;
  const top = block.y * CELL_H;
  const width = block.w * CELL_W;
  const height = block.h * CELL_H;

  const renderContent = () => {
    switch (block.type) {
      case 'title':
        return (
          <div className="w-full h-full flex items-center px-2 bg-[#111111]">
            <p className="text-white font-serif font-bold text-sm uppercase tracking-widest truncate">
              {block.content.title || 'TÍTULO DA SEÇÃO'}
            </p>
          </div>
        );

      case 'article':
        return (
          <div className="w-full h-full flex flex-col overflow-hidden">
            {block.content.imageUrl && block.h > 4 && (
              <div className="flex-shrink-0" style={{ height: Math.min(height * 0.45, 120) }}>
                <img
                  src={block.content.imageUrl}
                  alt=""
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}
            <div className="flex-1 p-2 overflow-hidden flex flex-col">
              {block.content.section && (
                <p className={`text-[8px] font-bold font-sans uppercase tracking-wide mb-0.5 ${sectionColor[block.content.section] || 'text-[#C41230]'}`}>
                  {block.content.section}
                </p>
              )}
              <p className={`font-serif font-bold text-[#111111] leading-tight mb-1 overflow-hidden ${
                block.content.style === 'headline' ? 'text-sm' :
                block.content.style === 'subheadline' ? 'text-xs' :
                'text-[10px]'
              }`} style={{ display: '-webkit-box', WebkitLineClamp: block.h > 3 ? 4 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {block.content.title || 'Título do artigo'}
              </p>
              {block.h > 3 && block.content.body && (
                <p className="text-[9px] text-[#555555] font-sans leading-tight overflow-hidden flex-1"
                  style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {block.content.body}
                </p>
              )}
              {block.content.author && (
                <p className="text-[8px] text-[#999999] font-sans mt-auto pt-1 border-t border-[#F0F0F0]">
                  {block.content.author}
                </p>
              )}
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="w-full h-full relative overflow-hidden">
            {block.content.imageUrl ? (
              <img src={block.content.imageUrl} alt="" className="w-full h-full object-cover object-top" />
            ) : (
              <div className="w-full h-full bg-[#F0F0F0] flex items-center justify-center">
                <i className="ri-image-line text-[#CCCCCC] text-2xl"></i>
              </div>
            )}
            {block.content.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                <p className="text-[8px] text-white font-sans">{block.content.caption}</p>
              </div>
            )}
          </div>
        );

      case 'ad':
        return (
          <div className="w-full h-full relative overflow-hidden bg-[#F8F8F8]">
            {block.content.imageUrl ? (
              <img src={block.content.imageUrl} alt="" className="w-full h-full object-cover object-top" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                <i className="ri-advertisement-line text-[#CCCCCC] text-xl"></i>
                <p className="text-[9px] text-[#CCCCCC] font-sans">Anúncio</p>
              </div>
            )}
            <div className="absolute top-1 right-1 bg-[#111111]/70 px-1 py-0.5">
              <p className="text-[7px] text-white font-sans uppercase tracking-wide">Publi</p>
            </div>
          </div>
        );

      case 'quote':
        return (
          <div className="w-full h-full flex items-center p-3 border-l-4 border-[#C41230] bg-[#F8F8F8]">
            <div>
              <i className="ri-double-quotes-l text-[#C41230] text-lg mb-1 block"></i>
              <p className="text-xs font-serif italic text-[#111111] leading-snug">
                {block.content.body || 'Citação em destaque'}
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-[#F0F0F0]">
            <p className="text-[10px] text-[#999999] font-sans">{block.type}</p>
          </div>
        );
    }
  };

  return (
    <div
      className={`absolute border transition-all ${
        isSelected
          ? 'border-[#C41230] ring-1 ring-[#C41230] z-20'
          : 'border-[#E0E0E0] hover:border-[#999999] z-10'
      } ${isDragging ? 'opacity-80 z-30' : ''} ${block.locked ? 'cursor-not-allowed' : 'cursor-move'} ${
        block.type === 'article' && block.content.priority
          ? `border-t-2 ${priorityBorder[block.content.priority] || ''}`
          : ''
      }`}
      style={{ left, top, width, height }}
      onMouseDown={handleMouseDown}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      {renderContent()}

      {/* Lock indicator */}
      {block.locked && (
        <div className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center bg-[#111111]/70">
          <i className="ri-lock-line text-white text-[8px]"></i>
        </div>
      )}

      {/* Resize handle (bottom-right) */}
      {isSelected && !block.locked && (
        <div className="absolute bottom-0 right-0 w-3 h-3 bg-[#C41230] cursor-se-resize z-30"></div>
      )}
    </div>
  );
}

export default function EditorCanvas({
  page,
  selectedBlockId,
  onSelectBlock,
  onUpdateBlock,
  onDropBlock,
  zoom,
}: EditorCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const scale = zoom / 100;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.floor((e.clientX - rect.left) / (CELL_W * scale));
    const y = Math.floor((e.clientY - rect.top) / (CELL_H * scale));
    const blockType = e.dataTransfer.getData('blockType');
    const extraData: Record<string, string> = {};
    if (blockType === 'article') extraData.articleId = e.dataTransfer.getData('articleId');
    if (blockType === 'image') extraData.imageUrl = e.dataTransfer.getData('imageUrl');
    if (blockType === 'ad') extraData.adId = e.dataTransfer.getData('adId');
    onDropBlock(blockType, Math.max(0, Math.min(COLS - 2, x)), Math.max(0, Math.min(ROWS - 2, y)), extraData);
  }, [scale, onDropBlock]);

  const canvasWidth = COLS * CELL_W;
  const canvasHeight = ROWS * CELL_H;

  return (
    <div
      className="flex-1 overflow-auto bg-[#E8E8E8] flex items-start justify-center p-8"
      onClick={() => onSelectBlock(null)}
    >
      <div
        style={{ transform: `scale(${scale})`, transformOrigin: 'top center', width: canvasWidth, minHeight: canvasHeight }}
      >
        {/* Page shadow */}
        <div className="relative" style={{ width: canvasWidth, height: canvasHeight }}>
          {/* Paper */}
          <div
            ref={canvasRef}
            className="absolute inset-0 bg-white"
            style={{ width: canvasWidth, height: canvasHeight }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* Grid lines */}
            <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.15 }}>
              {Array.from({ length: COLS + 1 }).map((_, i) => (
                <div
                  key={`col-${i}`}
                  className="absolute top-0 bottom-0 border-l border-[#0000FF]"
                  style={{ left: i * CELL_W }}
                />
              ))}
              {Array.from({ length: ROWS + 1 }).map((_, i) => (
                <div
                  key={`row-${i}`}
                  className="absolute left-0 right-0 border-t border-[#0000FF]"
                  style={{ top: i * CELL_H }}
                />
              ))}
            </div>

            {/* Page header */}
            <div className="absolute top-0 left-0 right-0 h-px bg-[#111111] z-10"></div>

            {/* Blocks */}
            {page.blocks.map((block) => (
              <BlockRenderer
                key={block.id}
                block={block}
                isSelected={selectedBlockId === block.id}
                onSelect={() => onSelectBlock(block.id)}
                onUpdate={(updates) => onUpdateBlock(block.id, updates)}
              />
            ))}

            {/* Empty state */}
            {page.blocks.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
                <div className="w-16 h-16 flex items-center justify-center border-2 border-dashed border-[#CCCCCC]">
                  <i className="ri-drag-drop-line text-[#CCCCCC] text-2xl"></i>
                </div>
                <p className="text-sm text-[#CCCCCC] font-sans">Arraste elementos ou use a IA para gerar o layout</p>
              </div>
            )}
          </div>

          {/* Page number */}
          <div className="absolute -bottom-6 left-0 right-0 flex items-center justify-center">
            <p className="text-[10px] text-[#999999] font-sans">Página {page.number} — {page.section}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
